import type { AutomationScheduleDto, AutomationScheduleSettings } from '@tms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button, Dialog, ErrorState, Field, Input, SkeletonRows } from '../../components/ui';
import { useErrorText, useFormat, useT } from '../../i18n';
import { api } from '../../lib/api-client';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query-keys';
import { useAutomationTeams } from './api';

const scheduleKey = [...queryKeys.automation.all, 'schedule'] as const;
const DAYS = [1, 2, 3, 4, 5, 6, 7] as const;
const DEFAULT_STEP_MINUTES = 10;

const toMinutes = (time: string): number => {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/** 'HH:MM' of minutes since midnight, held at 23:59 so a long list does not wrap into the night. */
const toTime = (minutes: number): string => {
  const held = Math.min(Math.max(minutes, 0), 23 * 60 + 59);
  return `${String(Math.floor(held / 60)).padStart(2, '0')}:${String(held % 60).padStart(2, '0')}`;
};

/**
 * The scheduled full run: a time (Almaty) and weekdays at which TMS starts the
 * same full run as the «Полный прогон» button, so results stay fresh between pushes.
 * Each project may start at its own time: projects sharing a test account must not
 * log in within the same minute.
 */
export const ScheduleDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useT();
  const { dateTime } = useFormat();
  const { errorText } = useErrorText();
  const queryClient = useQueryClient();
  const schedule = useQuery({
    queryKey: scheduleKey,
    queryFn: ({ signal }) =>
      api.get<AutomationScheduleDto>('/automation/schedule', undefined, signal),
    enabled: open,
  });
  const teams = useAutomationTeams();
  const projects = useMemo(
    () =>
      (teams.data ?? []).flatMap((team) =>
        team.projects.map((project) => ({ id: project.id, name: project.name, team: team.name })),
      ),
    [teams.data],
  );
  const [draft, setDraft] = useState<AutomationScheduleSettings | null>(null);
  const [step, setStep] = useState(String(DEFAULT_STEP_MINUTES));
  useEffect(() => {
    if (schedule.data && !draft) {
      const { enabled, time, days, projectIds, environment, projectTimes } = schedule.data;
      // a server from before per-project times sends none
      setDraft({ enabled, time, days, projectIds, environment, projectTimes: projectTimes ?? {} });
    }
  }, [schedule.data, draft]);

  const save = useMutation({
    mutationFn: (value: AutomationScheduleSettings) =>
      api.put<AutomationScheduleDto>('/automation/schedule', value),
    onSuccess: (saved) => {
      queryClient.setQueryData(scheduleKey, saved);
      toast.success(t('automation.schedule.saved'));
    },
    onError: (error) => toast.error(errorText(error)),
  });

  const data = schedule.data;
  const included = (id: string) =>
    !draft || draft.projectIds === null || draft.projectIds.includes(id);
  const timeOf = (id: string) => (draft ? (draft.projectTimes[id] ?? draft.time) : '');
  const chosen = projects.filter((project) => included(project.id));

  const toggleProject = (id: string) =>
    setDraft((current) => {
      if (!current) return current;
      const now = projects.filter((p) => included(p.id)).map((p) => p.id);
      const next = now.includes(id) ? now.filter((p) => p !== id) : [...now, id];
      // every project ticked is «all», so a project added later is run too
      return { ...current, projectIds: next.length === projects.length ? null : next };
    });
  const setProjectTime = (id: string, time: string) =>
    setDraft((current) =>
      current ? { ...current, projectTimes: { ...current.projectTimes, [id]: time } } : current,
    );
  /** The chosen projects one after another from the common time, `step` minutes apart. */
  const spread = () =>
    setDraft((current) => {
      if (!current) return current;
      const minutes = Math.max(1, Number(step) || DEFAULT_STEP_MINUTES);
      const start = toMinutes(current.time);
      return {
        ...current,
        projectTimes: Object.fromEntries(
          chosen.map((project, index) => [project.id, toTime(start + index * minutes)]),
        ),
      };
    });
  // projects still starting together after the edit: the thing this list is for
  const together = Object.entries(
    chosen.reduce<Record<string, string[]>>((byTime, project) => {
      const time = timeOf(project.id);
      byTime[time] = [...(byTime[time] ?? []), project.name];
      return byTime;
    }, {}),
  )
    .filter(([, names]) => names.length > 1)
    .sort(([a], [b]) => a.localeCompare(b));

  const toggleDay = (day: number) =>
    setDraft((current) =>
      current
        ? {
            ...current,
            days: current.days.includes(day)
              ? current.days.filter((d) => d !== day)
              : [...current.days, day].sort((a, b) => a - b),
          }
        : current,
    );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('automation.schedule.title')}
      description={t('automation.schedule.hint')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.close')}</Button>
          <Button
            variant="primary"
            disabled={!draft || save.isPending}
            loading={save.isPending}
            onClick={() => draft && save.mutate(draft)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      {schedule.error ? (
        <ErrorState error={schedule.error} onRetry={() => void schedule.refetch()} />
      ) : !data || !draft ? (
        <SkeletonRows rows={3} />
      ) : (
        <div className="flex flex-col gap-4 text-[13px]">
          <label className="flex items-center gap-2 text-ink">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
              className="size-4 rounded border-border"
            />
            {t('automation.schedule.enabled')}
          </label>

          <div className="flex flex-wrap items-end gap-4">
            <Field label={t('automation.schedule.time')} className="w-36">
              {({ id }) => (
                <Input
                  id={id}
                  type="time"
                  value={draft.time}
                  onChange={(event) => setDraft({ ...draft, time: event.target.value })}
                />
              )}
            </Field>
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-ink">
                {t('automation.schedule.days')}
              </span>
              <div className="flex gap-1" role="group" aria-label={t('automation.schedule.days')}>
                {DAYS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={draft.days.includes(day)}
                    onClick={() => toggleDay(day)}
                    className={cn(
                      'h-9 w-10 rounded-md border text-[12px] font-medium',
                      draft.days.includes(day)
                        ? 'border-accent bg-accent-soft text-accent-fg'
                        : 'border-border text-ink-muted hover:bg-sunken',
                    )}
                  >
                    {t(`automation.schedule.day.${day}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-[13px] font-medium text-ink">
              {t('automation.schedule.projects')}
            </legend>
            <p className="text-ink-muted">{t('automation.schedule.projectsHint')}</p>
            <div className="flex flex-wrap items-end gap-2">
              <Field label={t('automation.schedule.step')} className="w-28">
                {({ id }) => (
                  <Input
                    id={id}
                    type="number"
                    min={1}
                    max={120}
                    value={step}
                    onChange={(event) => setStep(event.target.value)}
                  />
                )}
              </Field>
              <Button onClick={spread} disabled={chosen.length === 0}>
                {t('automation.schedule.spread')}
              </Button>
            </div>
            {teams.error ? (
              <ErrorState error={teams.error} onRetry={() => void teams.refetch()} />
            ) : !teams.data ? (
              <SkeletonRows rows={3} />
            ) : (
              <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border">
                {projects.map((project) => (
                  <li key={project.id} className="flex items-center gap-3 px-3 py-1.5">
                    <input
                      type="checkbox"
                      checked={included(project.id)}
                      onChange={() => toggleProject(project.id)}
                      aria-label={t('automation.schedule.include', { name: project.name })}
                      className="size-4 rounded border-border"
                    />
                    <span className="min-w-0 flex-1 truncate text-ink">
                      {project.name}
                      <span className="ml-2 text-ink-subtle">{project.team}</span>
                    </span>
                    <Input
                      type="time"
                      className="w-28"
                      value={timeOf(project.id)}
                      disabled={!included(project.id)}
                      aria-label={t('automation.schedule.projectTime', { name: project.name })}
                      onChange={(event) => setProjectTime(project.id, event.target.value)}
                    />
                  </li>
                ))}
              </ul>
            )}
            {teams.data && chosen.length === 0 ? (
              <p className="text-fail">{t('automation.schedule.noProjects')}</p>
            ) : null}
            {together.map(([time, names]) => (
              <p key={time} className="text-skip">
                {t('automation.schedule.together', { time, names: names.join(', ') })}
              </p>
            ))}
          </fieldset>

          <p className="text-ink-muted">
            {draft.projectIds?.length
              ? t('automation.schedule.scopeSome', {
                  count: draft.projectIds.length,
                  environment: draft.environment,
                })
              : t('automation.schedule.scope', { environment: draft.environment })}{' '}
            {t('automation.schedule.catchUp')}
          </p>

          <div className="flex flex-col gap-1 rounded-md border border-border bg-sunken px-3 py-2">
            <p className="text-ink">
              {!data.enabled
                ? t('automation.schedule.off')
                : data.nextRunAt
                  ? Date.parse(data.nextRunAt) <= Date.now()
                    ? t('automation.schedule.dueNow')
                    : t('automation.schedule.next', { when: dateTime(data.nextRunAt) })
                  : t('automation.schedule.noDays')}
            </p>
            {/* only a start that ran: saving after the time also marks today, without a run */}
            {data.lastOn && data.lastFullRunId ? (
              <p className="text-ink-muted">
                {t('automation.schedule.last', { date: data.lastOn })}
                {' · '}
                <Link
                  to="/automation/full-run"
                  search={{ id: data.lastFullRunId }}
                  className="text-accent hover:underline"
                >
                  {t('automation.schedule.openLast')}
                </Link>
              </p>
            ) : null}
          </div>
        </div>
      )}
    </Dialog>
  );
};
